/**
 * Platform-wide directory search suggestions (recent / trending).
 * Brief in-memory cache to avoid hammering the public endpoint.
 */

import { useEffect, useState } from 'react';
import {
  createApiClient,
  type DirectorySuggestionsMode,
} from '@adieuu/shared';
import { useAppConfig } from '../config';

const CACHE_TTL_MS = 60_000;

type CacheEntry = {
  mode: DirectorySuggestionsMode;
  terms: string[];
  fetchedAt: number;
};

let memoryCache: CacheEntry | null = null;

export function clearPlatformSearchSuggestionsCache(): void {
  memoryCache = null;
}

export function usePlatformSearchSuggestions(enabled = true) {
  const { apiBaseUrl } = useAppConfig();
  const [mode, setMode] = useState<DirectorySuggestionsMode | null>(
    () => memoryCache?.mode ?? null,
  );
  const [terms, setTerms] = useState<string[]>(() => memoryCache?.terms ?? []);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const now = Date.now();
    if (memoryCache && now - memoryCache.fetchedAt < CACHE_TTL_MS) {
      setMode(memoryCache.mode);
      setTerms(memoryCache.terms);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    const api = createApiClient({ baseUrl: apiBaseUrl });

    void api.search
      .getSuggestions()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          const next: CacheEntry = {
            mode: res.data.mode,
            terms: Array.isArray(res.data.terms) ? res.data.terms : [],
            fetchedAt: Date.now(),
          };
          memoryCache = next;
          setMode(next.mode);
          setTerms(next.terms);
        } else {
          setMode(null);
          setTerms([]);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMode(null);
          setTerms([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, enabled]);

  return { mode, terms, isLoading };
}
